import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * BillingSuccess — post-Stripe-checkout landing.
 * Polls /api/billing/status/:session_id until payment_status transitions
 * from "pending" to "paid" (or fails). Refreshes user afterwards so the
 * new plan appears everywhere in the app.
 */
export default function BillingSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      count += 1;
      setAttempts(count);
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser?.();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (err) {
        if (cancelled) return;
      }
      if (count >= 30) {
        setStatus("timeout");
        return;
      }
      timerRef.current = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin mb-6" />
            <h1 className="font-display text-2xl font-bold mb-2">Confirmando seu pagamento...</h1>
            <p className="text-sm text-muted-foreground">
              Estamos conferindo com o Stripe. Isso leva poucos segundos.
            </p>
            <p className="text-xs text-muted-foreground mt-4 opacity-60">Tentativa {attempts}/30</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" data-testid="billing-success-icon" />
            <h1 className="font-display text-2xl font-bold mb-2">Assinatura confirmada!</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Bem-vindo(a) ao SmartStockAI. Todos os recursos do seu plano já estão liberados.
            </p>
            <Button
              data-testid="billing-goto-dashboard"
              onClick={() => navigate("/app/dashboard")}
              className="w-full h-12 bg-primary hover:bg-primary/90"
            >
              Ir para o Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-2xl font-bold mb-2">Pagamento não concluído</h1>
            <p className="text-sm text-muted-foreground mb-6">
              O pagamento não foi processado. Você pode tentar novamente sem prejuízo.
            </p>
            <Button
              onClick={() => navigate("/pricing")}
              variant="outline"
              className="w-full h-12"
            >
              Voltar para os planos
            </Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-6" />
            <h1 className="font-display text-2xl font-bold mb-2">Ainda processando</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Seu pagamento pode levar mais alguns minutos. Você receberá um email quando for confirmado. Enquanto isso, pode navegar pelo app.
            </p>
            <Button
              onClick={() => navigate("/app/dashboard")}
              className="w-full h-12 bg-primary hover:bg-primary/90"
            >
              Ir para o Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
